import React, { useState, useEffect, useRef }  from 'react';
import styles from 'components/quote_builder_widget/_style.module.scss';
import QuoteBuilderForm from 'components/quote_builder_widget/QuoteBuilderForm.jsx';
import ThreeDPreview from 'components/quote_builder_widget/ThreeDPreview.jsx';
import { GetRequest, PostRequest } from 'components/common/index.js';
import { getTheme } from 'components/quote_builder_widget/themes';

const QuoteBuilderWidget = ( {
  defaultId,
  templateIdentifier,
  theme: themeName = 'default',
  unitOption = 'both',
  widgetIdentifier,
  widgetFormToken: initialToken,
  widgetTokenRefreshMinutes = 25
} ) => {
  const [id, setId] = useState(defaultId)
  const theme = getTheme(themeName);
  const myRef = useRef();
  const [drawing, setDrawing] = useState({})
  const [loading, setLoading] = useState(defaultId != null)

  // Token state for secure form submission
  const [widgetFormToken, setWidgetFormToken] = useState(initialToken);
  const widgetFormTokenRef = useRef(initialToken);
  const tokenRefreshIntervalMilliseconds = widgetTokenRefreshMinutes * 60 * 1000;

  // Keep ref in sync with state (needed for interval callback)
  useEffect(() => {
    widgetFormTokenRef.current = widgetFormToken;
  }, [widgetFormToken]);

  // Auto-refresh token at configured interval
  useEffect(() => {
    if (!widgetIdentifier || !initialToken) {
      return;
    }

    const refreshToken = () => {
      // Send current token to get a new one (chain of trust)
      PostRequest({
        url: `/widget/quote-builder/${widgetIdentifier}/refresh-token`,
        data: { current_token: widgetFormTokenRef.current },
        callback: (data) => {
          if (data.widget_form_token) {
            setWidgetFormToken(data.widget_form_token);
          }
        }
      });
    };

    const interval = setInterval(refreshToken, tokenRefreshIntervalMilliseconds);
    return () => clearInterval(interval);
  }, [widgetIdentifier, tokenRefreshIntervalMilliseconds, initialToken]);

  // Extra pixels to account for card margin/border that may not be included in scrollHeight
  const BOTTOM_PADDING_BUFFER = 12;

  const publishSize = (height = null) => {
    if (drawing.catalog_name === undefined) {
      return;
    }

    if (window.parent !== undefined) {
      const baseHeight = height || (myRef.current ? myRef.current.scrollHeight : null);
      const calculatedHeight = baseHeight ? baseHeight + BOTTOM_PADDING_BUFFER : null;
      const message = {
        type: 'diecuttemplates.iframe_set_size',
        data: {
          identifier: templateIdentifier,
          width: String(theme.maxWidth || 800),
          height: calculatedHeight ? String(calculatedHeight) : null,
        },
      };
      window.parent.postMessage(message, "*");
    }
  }

  // Set up ResizeObserver to track content height changes
  const setupResizeObserver = () => {
    if (!myRef.current || loading || !drawing.catalog_name) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize || entry.target.scrollHeight;
        publishSize(height);
      }
    });

    resizeObserver.observe(myRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }

  const getDrawing = () => {
    // https://diecuttemplates.com.dev/drawing-data/72606
    if(id == null) { return }

    setLoading(true)
    GetRequest({
      url: `/drawing-data/${id}/${window.locale}`,
      callback: (data) => {
        setDrawing( () => data.drawing)
        setLoading(false)
      },
      404: () => {
        setLoading(false)
      },
      500: () => {
        setLoading(false)
      }
    })
  }

  const onDrawingFetched = () => {
    if (drawing.catalog_name === undefined) { return }
    publishSize();
  }

  // Validation helpers for live preview
  const isValidHexColor = (color) => /^#[0-9a-fA-F]{6}$/.test(color);

  const ALLOWED_FONT_FAMILIES = [
    '',
    'Arial, sans-serif',
    'Helvetica, Arial, sans-serif',
    'Georgia, serif',
    'Verdana, sans-serif',
    'Tahoma, sans-serif',
    '"Trebuchet MS", sans-serif',
    '"Times New Roman", serif',
    '"Courier New", monospace',
    'system-ui, sans-serif',
  ];

  // Listen for style updates from parent window (for live preview)
  const setupStyleUpdateListener = () => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'diecuttemplates.update_css') {
        const styleElement = document.getElementById('user-custom-css');
        if (styleElement) {
          styleElement.textContent = event.data.css || '';
        }
      }
      if (event.data && event.data.type === 'diecuttemplates.update_background_color') {
        let styleElement = document.getElementById('widget-background-color');
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = 'widget-background-color';
          document.head.appendChild(styleElement);
        }
        // Validate hex color format before applying
        if (event.data.color && isValidHexColor(event.data.color)) {
          styleElement.textContent = `[data-widget-element="layout-container"] { background: ${event.data.color} !important; }`;
        } else {
          styleElement.textContent = '';
        }
      }
      if (event.data && event.data.type === 'diecuttemplates.update_font_family') {
        let styleElement = document.getElementById('widget-font-family');
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = 'widget-font-family';
          document.head.appendChild(styleElement);
        }
        // Validate font family is in allowed list before applying
        if (event.data.fontFamily && ALLOWED_FONT_FAMILIES.includes(event.data.fontFamily)) {
          styleElement.textContent = `[data-widget-element="layout-container"] { font-family: ${event.data.fontFamily} !important; }`;
        } else {
          styleElement.textContent = '';
        }
      }
      if (event.data && event.data.type === 'diecuttemplates.update_highlight_color') {
        let styleElement = document.getElementById('widget-highlight-color');
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = 'widget-highlight-color';
          document.head.appendChild(styleElement);
        }
        // Validate hex color format before applying
        if (event.data.color && isValidHexColor(event.data.color)) {
          styleElement.textContent = `:root { --dct-highlight-color: ${event.data.color} !important; }`;
        } else {
          styleElement.textContent = '';
        }
      }
      if (event.data && event.data.type === 'diecuttemplates.update_submit_button_color') {
        let styleElement = document.getElementById('widget-submit-button-color');
        if (!styleElement) {
          styleElement = document.createElement('style');
          styleElement.id = 'widget-submit-button-color';
          document.head.appendChild(styleElement);
        }
        // Validate hex color format before applying
        if (event.data.color && isValidHexColor(event.data.color)) {
          styleElement.textContent = `:root { --dct-submit-button-color: ${event.data.color} !important; } [data-widget-element="submit-button"] { background-color: ${event.data.color} !important; border-color: ${event.data.color} !important; }`;
        } else {
          styleElement.textContent = '';
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  };

  // useEffect
  useEffect(getDrawing, [id])
  useEffect(onDrawingFetched, [drawing])
  useEffect(setupStyleUpdateListener, [])
  useEffect(setupResizeObserver, [drawing, loading])

  const Layout = theme.QuoteBuilderLayout;

  return (
    <div ref={myRef}>
      <Layout drawing={drawing} loading={loading}>
        <QuoteBuilderForm
          id={id}
          setId={setId}
          drawing={drawing}
          theme={theme}
          unitOption={unitOption}
          widgetIdentifier={widgetIdentifier}
          widgetFormToken={widgetFormToken}
          onTokenRefresh={setWidgetFormToken}
        />
      </Layout>
    </div>
  );
}

export default QuoteBuilderWidget;
